import React, { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { formatDuration } from '../../utils/dateTime';
import { cellSx, headSx, CountPill as Count } from '../common/table/tableKit';

const COLUMNS = [
  { key: 'date', label: 'Day / sign-in' },
  { key: 'device', label: 'Device' },
  { key: 'profileView', label: 'Profile views', center: true },
  { key: 'duration', label: 'Time spent', center: true },
  { key: 'favorite', label: 'Favorites', center: true },
  { key: 'session', label: 'Sessions', center: true },
  { key: 'conversation', label: 'Conversations', center: true },
];

// One row per day; open it to see each sign-in that day.
const LogTable = ({ data }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  if (!data.length) {
    return (
      <Box sx={{ py: 6, textAlign: 'center', border: '1px solid #ECECEC', borderRadius: '12px' }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#424242' }}>No activity in this period</Typography>
        <Typography sx={{ fontSize: 13, color: '#757575' }}>Sign-ins, views and conversations show here as they happen.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ border: '1px solid #ECECEC', borderRadius: '12px', overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
        <thead>
          <tr>
            {COLUMNS.map((c) => <Box component="th" key={c.key} sx={{ ...headSx, textAlign: c.center ? 'center' : 'left' }}>{c.label}</Box>)}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => {
            const open = expandedRow === index;
            return (
              <React.Fragment key={index}>
                <Box component="tr" sx={{ cursor: 'pointer', '&:hover td': { bgcolor: '#FCFAF7' } }} onClick={() => setExpandedRow(open ? null : index)}>
                  <Box component="td" sx={{ ...cellSx, fontWeight: 600, color: '#1F1F1F', whiteSpace: 'nowrap' }}>
                    <IconButton size="small" sx={{ mr: 0.5, color: open ? '#BE1F1F' : '#5F6368' }} aria-label={open ? 'Hide sign-ins' : 'Show sign-ins'}>
                      {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                    </IconButton>
                    {row.date}
                    <Box component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 400, color: '#9AA0A6' }}>{row.logs?.length || 0} sign-in{row.logs?.length === 1 ? '' : 's'}</Box>
                  </Box>
                  <Box component="td" sx={cellSx} />
                  <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}><Count value={row.profileView} /></Box>
                  <Box component="td" sx={{ ...cellSx, textAlign: 'center', whiteSpace: 'nowrap' }}>{row.duration}</Box>
                  <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}><Count value={row.favorite} /></Box>
                  <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}><Count value={row.session} /></Box>
                  <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}><Count value={row.conversation} /></Box>
                </Box>
                {open && row.logs.map((log, logIndex) => (
                  <Box component="tr" key={logIndex} sx={{ '& td': { bgcolor: '#FAFAFA' } }}>
                    <Box component="td" sx={{ ...cellSx, pl: 6, color: '#5F6368' }}>{log.time}</Box>
                    <Box component="td" sx={{ ...cellSx, color: '#5F6368', whiteSpace: 'nowrap' }}>{[log?.device?.platform, log?.device?.browser].filter(Boolean).join(' · ') || '—'}</Box>
                    <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}>{log.profileView}</Box>
                    <Box component="td" sx={{ ...cellSx, textAlign: 'center', color: '#5F6368' }}>{formatDuration(log?.sessionInfo?.durationMin)}</Box>
                    <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}>{log.favorite}</Box>
                    <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}>{log.session}</Box>
                    <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}>{log.conversation}</Box>
                  </Box>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </Box>
  );
};

export default LogTable;
